import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import TrustIndicators from './components/TrustIndicators';
import GuestCheckoutOption from './components/GuestCheckoutOption';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/ui/Footer';
import CustomerHeader from '../../components/ui/CustomerHeader';


const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flashMessage = location.state?.message;
  const { isAuthenticated, user } = useAuth();

  // Redirect if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      if (user?.role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/user-account-dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSocialLogin = async (socialData) => {
    try {
      // Mock social login implementation
      const sessionData = {
        email: socialData?.userData?.email,
        role: 'user', // Default role for social login
        provider: socialData?.provider,
        loginTime: new Date()?.toISOString(),
        userData: socialData?.userData
      };

      localStorage.setItem('userSession', JSON.stringify(sessionData));
      navigate('/user-account-dashboard');
    } catch (error) {
      console.error('Social login failed:', error);
    }
  };

  // Don't render login form if user is already authenticated
  if (isAuthenticated()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader
        cartItemCount={0}
        onCartClick={() => {}}
      />

      <div className="pt-16 flex-1">
        <Helmet>
          <title>Sign In - Bounderlaster | Access Your Beauty Account</title>
          <meta name="description" content="Sign in to your Bounderlaster account to access your wishlist, order history, and exclusive beauty deals. Secure login with social options available." />
          <meta name="keywords" content="beauty login, skincare account, makeup signin, beauty products, cosmetics login" />
        </Helmet>
          {/* Main Content */}
          <main className="flex-1">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Hero Content */}
                <div className="space-y-8">
                  <div className="text-center lg:text-left">
                    <h1 className="text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-4">
                      Welcome Back to Your
                      <span className="text-primary block">Beauty Journey</span>
                    </h1>
                    <p className="text-lg text-muted-foreground mb-8">
                      Sign in to access your personalized beauty recommendations, wishlist, and exclusive member benefits.
                    </p>
                  </div>

                  {/* Hero Image */}
                  <div className="relative rounded-2xl overflow-hidden shadow-elevation-2">
                    <Image
                      src="https://bounderlaster.com/uploads/products/just-me-oatmeal-honey/oatmeal-honey-model-lifestyle.png"
                      alt="Bounderlaster Just Me Oat & Honey body bar in use"
                      className="w-full h-64 lg:h-80 object-cover" />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Heart" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Wishlist</h3>
                      <p className="text-xs text-muted-foreground">Save your favorite products</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Package" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Order History</h3>
                      <p className="text-xs text-muted-foreground">Track your purchases</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Star" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Reviews</h3>
                      <p className="text-xs text-muted-foreground">Share your experiences</p>
                    </div>
                    <div className="p-4 bg-card rounded-lg border border-border">
                      <Icon name="Gift" size={24} className="text-primary mb-2" />
                      <h3 className="text-sm font-medium text-foreground mb-1">Rewards</h3>
                      <p className="text-xs text-muted-foreground">Earn points & discounts</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Login Form */}
                <div className="space-y-8">
                  <div className="bg-card rounded-2xl border border-border shadow-elevation-2 p-8">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                        Sign In to Your Account
                      </h2>
                      <p className="text-muted-foreground">
                        Access your beauty profile and continue shopping
                      </p>
                    </div>

                    {/* Social Login disabled — Google/Facebook OAuth not yet wired. */}

                    {flashMessage && (
                      <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        {flashMessage}
                      </div>
                    )}
                    {/* Login Form */}
                    <LoginForm isLoading={false} externalLoading={false} />
                  </div>

                  {/* Trust Indicators */}
                  <TrustIndicators />

                  {/* Guest Checkout Option */}
                  <GuestCheckoutOption />
                </div>
              </div>
            </div>
          </main>

      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserLogin;